/**
 * A project film in the viewer.
 *
 * The embed itself, not a player component: the shared player opens its own
 * dialog in a portal of its own, which lands outside the viewer's stacking
 * context and paints behind its backdrop — the film was playing where nobody
 * could see it. An iframe inside the viewer's own box has nowhere else to go.
 *
 * The player is also why the poster came back with a second play button over
 * it. The reader has already pressed play — the tile they clicked was the
 * poster — so the film arrives running.
 */

/** The film's address as something that can be embedded, or null if it can't. */
const toEmbed = (url: string): string | null => {
    const youtube =
        /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{6,})/i.exec(
            url,
        );
    if (youtube) {
        return `https://www.youtube.com/embed/${youtube[1]}?autoplay=1&rel=0&playsinline=1`;
    }
    const vimeo = /vimeo\.com\/(?:video\/)?(\d+)/i.exec(url);
    if (vimeo) {
        return `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1`;
    }
    return null;
};

export const ProjectGalleryVideo = ({
    url,
    title,
}: {
    url: string;
    title?: string;
}) => {
    const embed = toEmbed(url);

    // A film hosted as a file rather than on a service: the browser plays it.
    if (!embed) {
        return (
            <video
                className="absolute inset-0 size-full"
                src={url}
                controls
                autoPlay
                playsInline
            />
        );
    }

    return (
        <iframe
            className="absolute inset-0 size-full border-0"
            src={embed}
            title={title || 'Project video'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
        />
    );
};
